import { GoogleMap } from '@angular/google-maps';
import { Observable, Subscription } from 'rxjs';
import * as Tool from '../shared/threeDTools';

export interface Point {
    name: string,
    lat: number,
    lng: number,
}

export class ZoneTransform {
    points: Point[] = [];
    markers: any[] = [];
    midpointMarkers: any[] = [];
    map!: GoogleMap;
    onUpdate: any;
    onMarkerClick: any;
    onMarkerRightClick!: Function;
    isEnabled: boolean = false;
    zone: any;
    polygon: any;
    icons: any;
    polygonObj!: google.maps.Polygon;
    zonePoints: any[] = [];
    mergingPointObs!: Observable<any>;
    mergingPointSub!: Subscription;
    mergingPoint: any = false;
    zoneUpdated: boolean = false;
    prevPaths: any[] = [];
    redoPaths: any[] = [];
    svgIcon;
    focusSvgIcon;

    constructor(options: any) {
        this.svgIcon = {
            path: 'M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0',
            fillColor: "white",
            fillOpacity: 1,
            strokeColor: "black",
            strokeWeight: 1,
            scale: 0.4,
            anchor: new google.maps.Point(0, 0),
        };

        this.focusSvgIcon = {
            ...this.svgIcon,
            fillColor: "gray"
        }
        this.init(options);
    } 

    init(options: any) {
        if (options.onUpdate)
            this.onUpdate = options.onUpdate;
        if (options.onMarkerClick)
            this.onMarkerClick = options.onMarkerClick;
        if (options.map) 
            this.map = options.map;
        if (options.mergingPointObs)
            this.mergingPointObs = options.mergingPointObs;
        if (options.onMarkerRightClick)
            this.onMarkerRightClick = options.onMarkerRightClick;
        this.zonePoints = options.zonePoints;
        this.zone = options.zone;
        this.polygon = options.polygon;
        this.polygonObj = options.polygonObj;
        this.disable();
        this.enable();
    }

    enable() {
        let array = this.polygon.getArray();

        array.forEach((p: any, i: number) => {
            let point: Point = { name: i.toString(), lat: p.lat(), lng: p.lng() };
            this.points.push(point)
            this.addMarker(point);

            let nextIndex = (i + 1) % array.length; // Wrap around to the first point for the last edge
            let nextPoint = array[nextIndex];
            
            let [latMid, lngMid] = this.calculateMidpoint(
                p.lat(),
                p.lng(),
                nextPoint.lat(),
                nextPoint.lng()
            );
        
            let midpoint: Point = { name: `mid-${i}`, lat: latMid ?? 0, lng: lngMid ?? 0 };
            this.addMidpointMarker(midpoint);
        });

        this.polygonObj.addListener("dragstart", (event: any) => {
            this.addToPrevPath();
        });

        this.polygonObj.addListener("dragend", (event: any) => {
            let path = this.polygonObj.getPath().getArray().map(latLng => ({
                lat: latLng.lat(),
                lng: latLng.lng()
            }));
            this.redoPaths = [];
            this.updatePolygon();
        });

        this.polygonObj.setDraggable(true);
        this.mergingPointSub = this.mergingPointObs.subscribe(mergingPoint => {
            this.mergingPoint = mergingPoint;
        })
        this.isEnabled = true;
    }

    addToPrevPath() {
        let path = this.polygonObj.getPath().getArray().map(latLng => ({
            lat: latLng.lat(),
            lng: latLng.lng()
        }));
        this.prevPaths.push(path);
        return path;
    } 

    setRedoPath() {
        let path = this.polygonObj.getPath().getArray().map(latLng => ({
            lat: latLng.lat(),
            lng: latLng.lng()
        }));
        this.redoPaths = [path];
    }

    cancelAction() {
        if (this.prevPaths.length) {
            let pathCoords = this.prevPaths.pop();

            let currentPath = this.polygonObj.getPath().getArray().map(latLng => ({
                lat: latLng.lat(),
                lng: latLng.lng()
            }));
            this.redoPaths.unshift(currentPath);
            const path = new google.maps.MVCArray(
                pathCoords.map((coord: any) => new google.maps.LatLng(coord.lat, coord.lng))
            );
            this.updatePolygon(path);
        }
    }

    redoAction() {
        if (this.redoPaths.length) {
            let pathCoords = this.redoPaths.shift();
            let currentPath = this.polygonObj.getPath().getArray().map(latLng => ({
                lat: latLng.lat(),
                lng: latLng.lng()
            }));
            this.prevPaths.push(currentPath);
            const path = new google.maps.MVCArray(
                pathCoords.map((coord: any) => new google.maps.LatLng(coord.lat, coord.lng))
            );
            this.updatePolygon(path);
        }

    }


    updatePolygon(path: any = null) {
        if (!path) {
            path = this.polygonObj.getPath();
        } else {
            this.polygonObj.setPath(path);
        }
        const pathArray = this.polygonObj.getPath().getArray();
        // Get the array of LatLng objects that make up the path
        this.points = [];
        this.markers.forEach(m => m.marker.setMap(null));
        this.midpointMarkers.forEach(m => m.marker.setMap(null));
        this.midpointMarkers = [];
        this.markers = [];
        this.zonePoints.forEach(p => p.duplicateMarker?.getVisible() ? p.duplicateMarker.setMap(null) : null);
        pathArray.forEach((p, i) => {
            let point: Point = { name: i + "", lat: p.lat(), lng: p.lng() };
            this.points.push(point)
            this.addMarker(point);
        });
        if (this.onUpdate) {
            this.onUpdate(this.points.map(p => { return { lat: p.lat, lng: p.lng } }));
        }
    }

    disable() {
        this.markers.forEach(m => m.marker.setMap(null));
        this.midpointMarkers.forEach(m => m.marker.setMap(null));
        this.markers = [];
        this.points = [];
        this.midpointMarkers = [];
        this.isEnabled = false;
        if (this.mergingPointSub) {
            this.mergingPointSub.unsubscribe();
            this.mergingPointSub = null as any;
        }
    }

    addMarker(point: any, index: any = null) {
        let marker = new google.maps.Marker({
            position: { lat: point.lat, lng: point.lng },
            icon: this.svgIcon,
            map: this.map.googleMap,
        })

        marker.setDraggable(true);

        google.maps.event.addListener(marker, 'click', () => {

            let pos: any = marker.getPosition();
            point.lat = pos.lat();
            point.lng = pos.lng();

            if (this.onMarkerClick) {
                this.onMarkerClick({ marker, point });
            }
        });
        google.maps.event.addListener(marker, 'dragstart', () => {
            this.addToPrevPath();
        })

        google.maps.event.addListener(marker, 'dragend', () => {
            this.zoneUpdated = false;
            let pos: any = marker.getPosition();
            point.lat = pos.lat();
            point.lng = pos.lng();
            this.redoPaths = [];
            if (!this.mergingPoint) {
                this.updateMarker(point);
                if (!this.zoneUpdated) {
                    this.onUpdate(this.points.map(p => { return { lat: p.lat, lng: p.lng } }));
                }
                return;
            }
            let nearestPoint:any = this.mergingPoint;
            if (nearestPoint) {
                let mergedPos = nearestPoint.marker.getPosition()
                marker.setPosition(mergedPos);
                point.lat = mergedPos.lat();
                point.lng = mergedPos.lng();
                this.zoneUpdated = true;
                if (nearestPoint.isUnit)
                    nearestPoint.marker.setLabel({ ...nearestPoint.marker.getLabel(), className: "marker-label" });
                else
                    nearestPoint.hoverMarker.setVisible(false);
                this.onUpdate(this.points.map(p => { return { lat: p.lat, lng: p.lng } }));
            }
        });
        google.maps.event.addListener(marker, 'rightclick', (event: any) => {
            this.blurPoint();
            marker.setIcon(this.focusSvgIcon);
            this.onMarkerRightClick({ point });
        });


        if (index == null)
            this.markers.push({ name: point.name, marker });
        else
            this.markers.splice(index, 0, { name: point.name, marker });
    }

    addMidpointMarker(point: any) {
        const svgMidpointMarker = {
            path: 'M-20,0a20,20 0 1,0 40,0a20,20 0 1,0 -40,0',
            fillColor: "white",
            fillOpacity: 0.5, 
            strokeColor: "blue",
            strokeWeight: 0.5,
            scale: 0.2, 
            anchor: new google.maps.Point(0, 0)
        };
    
        const svgMidpointMarkerHovered = {
            ...svgMidpointMarker,
            scale: 0.4, 
        };
    
        let marker = new google.maps.Marker({
            position: { lat: point.lat, lng: point.lng },
            icon: svgMidpointMarker,
            map: this.map.googleMap,
        });
    
        marker.setDraggable(false);
    
        google.maps.event.addListener(marker, 'mouseover', () => {
            marker.setIcon(svgMidpointMarkerHovered);
        });
    
        google.maps.event.addListener(marker, 'mouseout', () => {
            marker.setIcon(svgMidpointMarker);
        });

        google.maps.event.addListener(marker, "click", () => {
            let [_, pointIndex] = point.name.match(/mid-(\d+)/);
            if (pointIndex) {
                this.redoPaths = [];
                this.addPoint(point, parseInt(pointIndex)+1);
            }
        })
    
        this.midpointMarkers.push({ name: point.name, marker });
    }

    addPoint(point: any, index: number | null = null): void {
        const newPoint = { ...point, lat: this.round(point.lat), lng: this.round(point.lng)};
    
        // this.points.push(newPoint);
    
        // this.addMarker(newPoint, index);
        let currentPath = this.addToPrevPath();
        const newPath = [...currentPath];
        if (index == null)
            newPath.push({ lat: newPoint.lat, lng: newPoint.lng });
        else
            newPath.splice(index, 0, { lat: newPoint.lat, lng: newPoint.lng })

        this.updatePolygon(newPath);
    }

    removePoint(point: any) {
        if (this.points.length > 3) {
            let currentPath = this.addToPrevPath();
            let index = this.points.findIndex(p => p.name == point.name);
            if (index == -1) return;
            const newPath = [...currentPath];
            newPath.splice(index, 1);

            this.updatePolygon(newPath);
        } else 
            this.blurPoint();
    }

    updatePoint(point: Point, position: { lat: number, lng: number }) {
        let currentPath = this.addToPrevPath();
        let index = this.points.findIndex(p => p.name == point.name);
        if (index == -1) return;
        const newPath = [...currentPath];
        newPath.splice(index, 1, { lat: position.lat, lng: position.lng })
        this.updatePolygon(newPath);
    }

    blurPoint() {
        this.markers.forEach(m => m.marker.setIcon(this.svgIcon));
    }

    round(num: number): number {
        return parseFloat(num.toFixed(7));
    }
    
    
    updateMarker(point: any) {
        let _marker = this.markers.find(m => m.name == point.name);
        if (_marker)
            _marker.marker.setPosition({ lat: point.lat, lng: point.lng })
    }

    calculateMidpoint(lat1: any, lon1: any, lat2: any, lon2: any) {
        const [x1, y1] = Tool.getXYFromLatLonAlt(lat1, lon1);
        const [x2, y2] = Tool.getXYFromLatLonAlt(lat2, lon2);
    
        const xMid = ((x1 ?? 0) + (x2 ?? 0)) / 2;
        const yMid = ((y1 ?? 0) + (y2 ?? 0)) / 2;
    
        const [lonMid, latMid] = Tool.getLatLonFromXYZ(xMid, yMid, 0);
    
        return [latMid, lonMid];
    };
}